import React, { useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Switch,
  FormControl,
  FormLabel,
  Autocomplete,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MDButton from "components/MDButton";
import DataTable from "comps/Tables/DataTable";
import TLinkApi from "services/tlink.api";
import MRTClientDataTable from "comps/Tables/DataTable/MRTClientDataTable";
import { MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";

const BASE_ENDPOINT = "/configurations/supplier-client";
const LANG_ENDPOINT = "/masters/language";

const parseAllowedLanguageId = (raw, languages) => {
  if (raw === -1 || raw === "-1") return -1;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (/^all\s+lan?guages?$/i.test(s)) return -1;
    const asNum = Number(s);
    if (Number.isFinite(asNum)) return asNum;
    const byName = languages.find((l) => l.name.toLowerCase() === s.toLowerCase());
    if (byName) return byName.id;
  }
  if (Number.isFinite(Number(raw))) return Number(raw);
  return -1;
};

const ClientBuyerConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [data, setData] = useState([]);

  const [languages, setLanguages] = useState([]);
  const languageOptions = useMemo(
    () => [{ id: -1, name: "All Languages" }, ...languages],
    [languages]
  );
  const languageMap = useMemo(
    () => Object.fromEntries(languages.map((l) => [l.id, l.name])),
    [languages]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [supplierId, setSupplierId] = useState("");
  const [clientId, setClientId] = useState("");
  const [allowedLanguageId, setAllowedLanguageId] = useState(-1);
  const [apiSupplier, setApiSupplier] = useState(false);
  const [isSupplierActive, setIsSupplierActive] = useState(false);
  const [isClientActive, setIsClientActive] = useState(false);
  const [isClientAllowed, setIsClientAllowed] = useState(false);

  const fetchLanguages = async () => {
    try {
      const res = await TLinkApi.get(LANG_ENDPOINT);
      const list =
        (res?.data ?? [])
          .map((x) => ({ id: Number(x.id), name: String(x.name) }))
          .sort((a, b) => a.name.localeCompare(b.name));
      setLanguages(list);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await TLinkApi.get(BASE_ENDPOINT);
      const rows = (res?.data ?? []).map((r, idx) => ({
        id: r.id ?? `${r.supplier_id}:${r.client_id}:${idx}`,
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        client_id: r.client_id,
        client_name: r.client_name,
        allowed_languages: r.allowed_languages, // raw (string/-1/id)
        api_supplier: !!r.api_supplier,
        is_supplier_active: !!r.is_supplier_active,
        is_client_active: !!r.is_client_active,
        is_client_allowed: !!r.is_client_allowed,
      }));
      setData(rows);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
    fetchData();
  }, []);

  const openAdd = () => {
    setMode("add");
    setEditingId(null);
    setSupplierId("");
    setClientId("");
    setAllowedLanguageId(-1);
    setApiSupplier(false);
    setIsSupplierActive(false);
    setIsClientActive(false);
    setIsClientAllowed(false);
    setErrorMsg("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    setEditingId(row.id ?? null);
    setSupplierId(String(row.supplier_id ?? ""));
    setClientId(String(row.client_id ?? ""));
    setAllowedLanguageId(parseAllowedLanguageId(row.allowed_languages, languages));
    setApiSupplier(Boolean(row.api_supplier));
    setIsSupplierActive(Boolean(row.is_supplier_active));
    setIsClientActive(Boolean(row.is_client_active));
    setIsClientAllowed(Boolean(row.is_client_allowed));
    setErrorMsg("");
    setModalOpen(true);
  };

  const valid =
    supplierId.trim() !== "" &&
    clientId.trim() !== "";

  const handleSave = async () => {
    if (!valid) {
      setErrorMsg("Supplier ID and Client ID are required.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        supplierId: Number(supplierId),
        clientId: Number(clientId),
        // single number: -1 (All) or a specific language id
        languageId: Number(allowedLanguageId),
      
        isAllowed: Boolean(isClientAllowed),
      };

      if (mode === "add") {
        await TLinkApi.post(BASE_ENDPOINT, payload);
      } else {
        if (editingId != null && Number.isFinite(Number(editingId))) {
          await TLinkApi.update(`${BASE_ENDPOINT}/${editingId}`, payload);
        } else {
          await TLinkApi.update(`${BASE_ENDPOINT}/${1}`, payload);
        }
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Something went wrong";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const oneLine = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  const columns = useMemo(
    () => [
      {
        accessor: "supplier_id",
        Header: "Supplier Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.supplier_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "supplier_name",
        Header: "Supplier Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.supplier_name || "";
          const display = name ? (name.length > 20 ? name.slice(0, 20) + "..." : name) : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "client_id",
        Header: "Client Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.client_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "client_name",
        Header: "Client Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.client_name || "";
          const display = name ? (name.length > 20 ? name.slice(0, 20) + "..." : name) : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "allowed_languages",
        Header: "Allowed Languages",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => {
          const raw = row.original.allowed_languages;
          let display = "-";
          if (
            raw
            // === -1 ||
            // raw === "-1" ||
            // (typeof raw === "string" && /^all\s+lan?guages?$/i.test(raw.trim()))
          ) {
            display = raw;
          }
          // else {
          //   const id = parseAllowedLanguageId(raw, languages);
          //   display = languageMap[id] ?? String(id);
          // }
          return (
            <Tooltip title={display}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "api_supplier",
        Header: "API Supplier",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.api_supplier ? "Yes" : "No"),
      },
      {
        accessor: "is_supplier_active",
        Header: "Supplier Active",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_supplier_active ? "Yes" : "No"),
      },
      {
        accessor: "is_client_active",
        Header: "Client Active",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_client_active ? "Yes" : "No"),
      },
      {
        accessor: "is_client_allowed",
        Header: "Client Allowed",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_client_allowed ? "Yes" : "No"),
      },
      {
        accessor: "actions",
        Header: "Actions",
        align: "right",
        headerAlign: "right",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" pr={1}>
            <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ p: 0.5 }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [languages, languageMap]
  );

  const [fullScr, setFullScr] = useState(false);
  const renderToolbarInternalActions = ({ table }) => {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "0rem" }}>
          <Button variant="contained" color="info" onClick={openAdd}>
            + New
          </Button>
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <div onClick={() => setFullScr(!fullScr)} >
            <MRT_ToggleFullScreenButton table={table} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      {isLoading ? (
        <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}

      {/* <DataTable
        table={{ columns, rows: data }}
        isSorted
        entriesPerPage={entriesPerPage}
        canSearch={false}
        tablePage={page}
        setTablePage={setPage}
        setEntriesPerPage={setEntriesPerPage}
        totalLength={data.length}
        pagination
        enableTopToolbar
        tableMaxHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        tableMinHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: window.innerWidth > 1600 ? "calc(76vw - 580px)" : "calc(76vw - 565px)", marginTop: "3px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Client Configuration
            </Typography>
            <Button variant="contained" color="info" onClick={openAdd}>
              + New
            </Button>
          </Box>
        )}
      /> */}
      <MRTClientDataTable
        table={{ columns, rows: data }}
        isSorted
        canSearch={false}
        enableTopToolbar
        defaultPageSize={50}
        entriesOption={["10", "20", "30", "40", "50"]}
        tableMaxHeight={"calc(100vh - 220px)"}
        tableMinHeight={"calc(100vh - 220px)"}
        // ✅ your custom actions bar gets rendered in the MRT toolbar
        renderToolbarInternalActions={renderToolbarInternalActions}
        fullScr={fullScr}
        renderTopToolbarCustomActions={() => (
          <Box
            // sx={{
            //   display: "flex",
            //   justifyContent: "space-between",
            //   alignItems: "center",
            //   width: window.innerWidth > 1600 ? "calc(76vw - 580px)" : "calc(76vw - 565px)",
            //   mt: "3px",
            // }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Client Configuration
            </Typography>
            {/* <Button variant="contained" color="info" onClick={openAdd}>
              + New
            </Button> */}
          </Box>
        )}
      />
      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={() => !submitting && setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{mode === "add" ? "Add" : "Update"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Supplier ID</FormLabel>
                <TextField
                  size="small"
                  type="number"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Client ID</FormLabel>
                <TextField
                  size="small"
                  type="number"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                />
              </FormControl>
            </Stack>

            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Allowed Languages</FormLabel>
              <Autocomplete
                options={languageOptions}
                value={
                  languageOptions.find((opt) => opt.id === allowedLanguageId) ??
                  languageOptions[0]
                }
                onChange={(_, selected) => setAllowedLanguageId(selected?.id ?? -1)}
                getOptionLabel={(opt) => opt?.name ?? ""}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Select one language" />
                )}
              />
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
             

              <FormControl>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Allowed</FormLabel>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={isClientAllowed} onChange={(e) => setIsClientAllowed(e.target.checked)} />
                  <Typography variant="body2">{isClientAllowed ? "Yes" : "No"}</Typography>
                </Stack>
              </FormControl>
            </Stack>

            {errorMsg ? (
              <Typography variant="body2" color="error">
                {errorMsg}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleSave} disabled={submitting || !valid}>
            {submitting ? (mode === "add" ? "Saving…" : "Updating…") : mode === "add" ? "Save" : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClientBuyerConfig;
